import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { Widget } from 'app/providers/bridge-api/dashboard.service';
import { Report, ReportService } from 'app/providers/storage-api/report.service';
import { MatDialogRef, MAT_DIALOG_DATA } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { WidgetDataSource } from '../../../../models';
import { TaxonomyMapWidgetOptions } from '../widget-taxonomy-map/widget-taxonomy-map.component';

export interface TaxonomySearchDialogOptions<T> {
  widget: Widget<T>;
  widgetDataSource: WidgetDataSource;
  projectId: string;
  reportFilter: string;
  reportQuery: string;
}

export interface TaxonomyNodeInfo {
  path: string;
  nodeName: string;
  depth: number;
  docCount: number;
  displayName?: string;
  parentId?: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-taxonomy-search-dialog',
  templateUrl: './taxonomy-search-dialog.component.html',
  styleUrls: ['./taxonomy-search-dialog.component.scss'],
  standalone: false,
})
export class TaxonomySearchDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  public query = '';
  public search$ = new Subject<string>();
  public widget: Widget;
  public reportFilter: string;
  public reportQuery: string;
  public projectId: string;
  public taxonomyNodes: TaxonomyNodeInfo[];
  public TaxonomyNodeLookup: {};
  public filteredTaxonomyNodes: TaxonomyNodeInfo[];
  public isLoading: boolean;

  constructor(
    private reportService: ReportService,
    private dialogRef: MatDialogRef<TaxonomySearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private options: TaxonomySearchDialogOptions<TaxonomyMapWidgetOptions>
  ) {
    this.widget = options.widget;
    this.projectId = options.projectId;
    this.taxonomyNodes = [];
    this.filteredTaxonomyNodes = [];
    this.reportFilter = options.reportFilter;
    this.reportQuery = options.reportQuery;
    this.TaxonomyNodeLookup = {};
  }

  async ngOnInit() {
    this.search$
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => this.filterTaxonomyNodes());
    await this.searchTaxonomy();
  }

  public onSearch(value: string) {
    this.query = value.trim();
    this.search$.next(this.query);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public async confirm(): Promise<void> {
    this.dialogRef.close();
  }

  public async searchTaxonomy() {
    this.isLoading = true;
    this.taxonomyNodes = [];
    const report = (await this.reportService.getReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      'taxonomySearch',
      {
        analysisName: this.options.widgetDataSource.analysisId,
        taxonomyQuery: this.query,
      },
      'json',
      this.reportQuery,
      this.reportFilter,
      null,
      this.projectId
    )) as Report<any>;

    if (!report.items) {
      this.isLoading = false;
      return;
    } else {
      for (const key of Object.getOwnPropertyNames(report.items)) {
        for (const entry of Object.getOwnPropertyNames(report.items[key])) {
          const currentNode: TaxonomyNodeInfo = {
            path: report.items[key][entry].path,
            depth: report.items[key][entry].depth,
            docCount: report.items[key][entry].docCount,
            displayName: report.items[key][entry].name,
            nodeName: key,
            parentId: report.items[key][entry].parentId,
          };
          this.taxonomyNodes.push(currentNode);
          this.TaxonomyNodeLookup[key] = currentNode;
        }
      }
      this.sortTaxonomyNodes();
      this.filterTaxonomyNodes();
    }
    this.isLoading = false;
  }

  public filterTaxonomyNodes() {
    if (this.query && this.query !== '') {
      this.filteredTaxonomyNodes = JSON.parse(
        JSON.stringify(
          this.taxonomyNodes.filter((node) =>
            node.displayName.toLowerCase().includes(this.query.toLowerCase())
          )
        )
      );
    } else {
      this.filteredTaxonomyNodes = JSON.parse(JSON.stringify(this.taxonomyNodes));
    }
  }

  private sortTaxonomyNodes() {
    this.taxonomyNodes.sort((a, b) => (a.path > b.path ? 1 : -1));
  }

  //We are setting to the parent of the selected node to display the selected node and
  //it's siblings on the taxonomy map.
  public setNode(node: TaxonomyNodeInfo) {
    this.widget.payload.taxonomyMentionField = undefined;
    this.widget.payload.isDrilledDown = false;
    this.widget.payload.drilledDownOn = 'none';
    let parts = [];
    if (node.path !== '') {
      parts = node.path.split('|');
    }
    if (parts.length < 1) {
      this.widget.payload.taxonomyPath = '';
      this.widget.payload.prevTaxonomyLevels = [];
    } else {
      this.widget.payload.drilledDownOn = 'taxonomy';
      this.widget.payload.isDrilledDown = true;
      //Set the parent as the current node
      this.widget.payload.taxonomyPath = parts.join('|');
      this.widget.payload.prevTaxonomyLevels = [];
      parts.pop();
      let continueLoop = true;
      if (parts.length > 0) {
        this.widget.payload.prevTaxonomyLevels.push(parts.join('|'));
      }
      while (continueLoop) {
        parts.pop();
        if (parts.length < 1) {
          continueLoop = false;
          break;
        }
        this.widget.payload.prevTaxonomyLevels.push(parts.join('|'));
      }
      this.widget.payload.prevTaxonomyLevels.push('');
      this.widget.payload.prevTaxonomyLevels = this.widget.payload.prevTaxonomyLevels.reverse();
    }
    this.dialogRef.close({ widget: this.widget });
  }

  public displayTaxonomyPath(node: TaxonomyNodeInfo) {
    return node.path ? node.path.split('|').join('/') : '';
  }

  ngOnDestroy(): void {
    this.search$.unsubscribe();
  }
}
